"use client";

import { Monitor, Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { cn } from "@/lib/utils";
import { useTheme } from "@/utils/themeProvider";

type ThemeMode = "light" | "dark" | "system";

interface AnimatedThemeTogglerProps
	extends React.ComponentPropsWithoutRef<"button"> {
	duration?: number;
	size?: "sm" | "md" | "lg";
	showLabel?: boolean;
}

const sizeConfig = {
	sm: { button: "w-8 h-8", icon: "w-4 h-4" },
	md: { button: "w-10 h-10", icon: "w-5 h-5" },
	lg: { button: "w-12 h-12", icon: "w-6 h-6" },
};

const themeOrder: ThemeMode[] = ["light", "dark", "system"];

const themeConfig: Record<
	ThemeMode,
	{ label: string; Icon: React.ComponentType<{ className?: string }> }
> = {
	light: { label: "Light", Icon: Sun },
	dark: { label: "Dark", Icon: Moon },
	system: { label: "System", Icon: Monitor },
};

export function AnimatedThemeToggler({
	className,
	duration = 400,
	size = "sm",
	showLabel = false,
	...props
}: AnimatedThemeTogglerProps) {
	const { theme, setTheme } = useTheme();
	const [mounted, setMounted] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	const currentTheme = (mounted ? (theme as ThemeMode) : null) ?? "system";
	const currentIndex = themeOrder.indexOf(currentTheme);
	const nextTheme = themeOrder[(currentIndex + 1) % themeOrder.length];

	const { button, icon } = sizeConfig[size];
	const { label, Icon } = themeConfig[currentTheme];
	const accessibleLabel = showLabel
		? `${label} theme, switch to ${nextTheme} mode`
		: `Switch to ${nextTheme} mode`;

	const toggleTheme = useCallback(async () => {
		if (!buttonRef.current) return;

		const disableCss = document.createElement("style");
		disableCss.appendChild(
			document.createTextNode(
				"*,*::before,*::after{transition:none!important}",
			),
		);
		document.head.appendChild(disableCss);
		// Force the browser to process the new style before the theme class changes
		(() => window.getComputedStyle(document.body))();
		const restoreTransitions = () => {
			(() => window.getComputedStyle(document.body))();
			setTimeout(() => document.head.removeChild(disableCss), 1);
		};

		if (!document.startViewTransition) {
			setTheme(nextTheme);
			restoreTransitions();
			return;
		}

		const transition = document.startViewTransition(() => {
			flushSync(() => {
				setTheme(nextTheme);
			});
		});

		await transition.ready;

		const { top, left, width, height } =
			buttonRef.current.getBoundingClientRect();
		const x = left + width / 2;
		const y = top + height / 2;
		const maxRadius = Math.hypot(
			Math.max(left, window.innerWidth - left),
			Math.max(top, window.innerHeight - top),
		);

		document.documentElement.animate(
			{
				clipPath: [
					`circle(0px at ${x}px ${y}px)`,
					`circle(${maxRadius}px at ${x}px ${y}px)`,
				],
			},
			{
				duration,
				easing: "ease-in-out",
				pseudoElement: "::view-transition-new(root)",
			},
		);

		transition.finished.then(restoreTransitions);
	}, [nextTheme, duration, setTheme]);

	return (
		<button
			ref={buttonRef}
			type="button"
			onClick={toggleTheme}
			className={cn(
				showLabel
					? "h-8 gap-1.5 px-2.5 font-mono text-sm uppercase tracking-[0.14em]"
					: button,
				"relative inline-flex items-center justify-center overflow-hidden",
				"border border-border bg-background text-muted-foreground",
				"hover:border-brand/40 hover:text-brand",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background",
				"transition-[color,border-color,transform] duration-150 active:scale-[0.96]",
				className,
			)}
			aria-label={accessibleLabel}
			{...props}
		>
			<Icon className={cn(icon, "shrink-0")} />
			{showLabel && <span className="font-mono text-sm">{label}</span>}
		</button>
	);
}
